/**
 * Pathway activities service (Sprint 4.3.2, PROD-651).
 *
 * Pure helpers that translate FHIR R4 `Task` resources (returned by
 * `getFhirClient().searchTasks`) into the orchestration-side
 * `PathwayActivity` shape consumed by the ActivityInspector and the
 * PatientPathwayTimeline.
 *
 * Reference (vault):
 *   03-knowledge-base/external-references/awell/orchestration/docs/
 *   getting-started/orchestration-ontology.md
 *
 * No I/O happens here; the caller passes the bundle entries fetched
 * from the FHIR client. This keeps the helper deterministic + easy to
 * test.
 *
 * HC1 reproducibility: pure functions; deterministic given the same
 * input. The PatientPathwayTimeline test seeds a fixed Bundle and
 * expects byte-stable order.
 *
 * HC5: zero em / en dashes anywhere.
 */
import type { Bundle, Task } from './fhir/types';
import {
  mapTaskStatusToActivityStatus,
  type PathwayActivity,
} from '../types/pathway-activity';

/**
 * Read the activity-mappable fields off a FHIR Task. The Task.id, owner
 * reference, and execution period feed the runtime-view fields the
 * inspector renders.
 *
 * Tasks that fail validation (missing id) are dropped via the caller so
 * the inspector list never carries half-baked rows.
 */
export function taskToPathwayActivity(task: Task): PathwayActivity | null {
  if (!task.id) return null;
  const period = (task as unknown as {
    executionPeriod?: { start?: string; end?: string };
  }).executionPeriod;
  const owner = (task as unknown as { owner?: { reference?: string } }).owner;
  const basedOn = (task as unknown as { basedOn?: Array<{ reference?: string }> })
    .basedOn;
  const instantiates = (task as unknown as { instantiatesCanonical?: string })
    .instantiatesCanonical;

  return {
    activity_id: task.id,
    action_def_id: instantiates ?? '',
    status: mapTaskStatusToActivityStatus(task.status),
    source_trigger_ref:
      basedOn && basedOn[0]?.reference
        ? basedOn[0].reference
        : 'system/orchestrator',
    start_date: period?.start ?? null,
    completion_date: period?.end ?? null,
    indirect_object: owner?.reference ?? 'unassigned',
    label: task.description ?? undefined,
  };
}

/**
 * Read a Task bundle into a PathwayActivity[]. Drops malformed entries
 * and keeps the bundle order so callers can apply their own sort
 * (timeline view sorts by start_date; multiplicity badge counts).
 */
export function bundleToPathwayActivities(
  bundle: Bundle<Task>,
): PathwayActivity[] {
  if (!bundle.entry) return [];
  const activities: PathwayActivity[] = [];
  for (const entry of bundle.entry) {
    if (!entry.resource) continue;
    const a = taskToPathwayActivity(entry.resource);
    if (a !== null) activities.push(a);
  }
  return activities;
}

/**
 * Filter `activities` to those instantiating a given action def. The
 * demo client search filter is a top-level string-equality check that
 * does not match `instantiatesCanonical`, so the caller does the
 * filter client-side. This keeps the demo + live paths interchangeable.
 */
export function filterActivitiesByActionDef(
  activities: PathwayActivity[],
  actionDefId: string,
): PathwayActivity[] {
  return activities.filter((a) => a.action_def_id === actionDefId);
}

/**
 * Sort activities by `start_date` ascending so the timeline view
 * renders chronologically. Activities without a start_date sink to
 * the bottom of the list (treated as the most-pending).
 */
export function sortActivitiesByStartDate(
  activities: PathwayActivity[],
): PathwayActivity[] {
  return [...activities].sort((a, b) => {
    if (a.start_date === null && b.start_date === null) {
      return a.activity_id.localeCompare(b.activity_id);
    }
    if (a.start_date === null) return 1;
    if (b.start_date === null) return -1;
    return a.start_date.localeCompare(b.start_date);
  });
}
